﻿import React from 'react';
import axios from 'axios';

function buildFormData(formData, data, parentKey?) {
    if (
        data &&
        typeof data === "object" &&
        !(data instanceof Date) &&
        !(data instanceof File)
    ) {
        Object.keys(data).forEach((key) => {
            buildFormData(
                formData,
                data[key],
                parentKey ? `${parentKey}[${key}]` : key
            );
        });
    } else {
        const value =
            data === null ? "" : data instanceof Date ? data.toJSON() : data;
        formData.append(parentKey, value);
    }
}

function jsonToFormDataMultipleParams(data) {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
    });

    return formData;
}

function jsonToFormData(data) {
    const formData = new FormData();
    buildFormData(formData, data);
    return formData;
}

export function callApi(url, params?) {
    if (params && Object.keys(params).length > 1) {
        return callApiInternal(url, jsonToFormDataMultipleParams(params));
    }
    return callApiInternal(url, jsonToFormData(params));
}

function callApiInternal(url, formData) {
    return window
        .fetch(url, {
            credentials: "same-origin",
            method: "POST",
            body: formData,
        })
        .then((r) => {
            return r.json();
        })
        .then((x) => {
            if (x.status === "Ok") {
                return x.result;
            }
            console.warn("callApi: " + url);
            console.warn(x);
            return Promise.reject();
        });
}

export async function apiFetch(url) {
    let o = await fetch(url, { method: 'GET' });
    let json = await o.json();
    return json.result;
}




///////////////////////////////////////////////////////////////////////////////////////////

/* AXIOS SETTINGS */
//const API_TIMEOUT = 5000;
//
//function sleep(ms) {
//    return new Promise(res => setTimeout(res, ms));
//}
//
//export const api = axios.create({
//    timeout: API_TIMEOUT
//});
//
///* ---- REQUEST INTERCEPTOR ---- */
//api.interceptors.request.use((config) => {
//    if (!config.signal) {
//        config.signal = AbortSignal.timeout(API_TIMEOUT);
//    }
//    return config;
//});
//
///* ---- RESPONSE INTERCEPTOR ---- */
//api.interceptors.response.use(
//    (response) => response,
//
//    async (error) => {
//        const config = error.config;
//        if (!config) return Promise.reject(error);
//
//        // Initialize retry counter
//        if (!config._retryCount) config._retryCount = 0;
//
//        // Retry up to 3 times
//        if (config._retryCount < 3) {
//            config._retryCount++;
//
//            // New abort controller for each retry
//            config.signal = AbortSignal.timeout(API_TIMEOUT);
//
//            // Exponential backoff
//            await sleep(2 ** config._retryCount * 800);
//
//            return api(config);
//        }
//
//        return Promise.reject(error);
//    }
//);

/* AXIOS SETTINGS */
const API_TIMEOUT = 1000 * 15;

function sleep(ms: number) {
    return new Promise(res => setTimeout(res, ms));
}

/* ---- CACHE SETUP ---- */
const cache = new Map<string, any>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCache(url: string) {
    const entry = cache.get(url);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > CACHE_TTL) {
        cache.delete(url);
        return null;
    }

    return entry.data;
}

function setCache(url: string, data: any) {
    cache.set(url, {
        timestamp: Date.now(),
        data,
    });
}

/* ---- AXIOS INSTANCE ---- */
export const api = axios.create({
    timeout: API_TIMEOUT,
});

/* ---- REQUEST INTERCEPTOR ---- */
api.interceptors.request.use((config) => {
    if (!config.signal) {
        config.signal = AbortSignal.timeout(API_TIMEOUT);
    }
    return config;
});

/* ---- RESPONSE INTERCEPTOR (retries) ---- */
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const config = error.config;
        if (!config) return Promise.reject(error);

        const method = config.method?.toLowerCase();

        // ❌ Only retry GET requests
        if (method !== 'get') {
            return Promise.reject(error);
        }

        if (!config._retryCount) {
            config._retryCount = 0;
        }

        // Max retries
        if (config._retryCount >= 3) {
            return Promise.reject(error);
        }

        config._retryCount++;

        // Reset abort signal for retry
        config.signal = AbortSignal.timeout(API_TIMEOUT);

        // Exponential backoff: 800ms, 1600ms, 3200ms
        await sleep(2 ** config._retryCount * 800);

        return api(config);
    }
);

/* ---- GRACEFUL FETCH HELPER ---- */
export async function gracefulGet(url: string, useGraceful = false) {
    if (!useGraceful) {
        // Normal behavior
        console.log("Not using graceful");
        const response = await api.get(url);
        return response;
    }

    const cached = getCache(url);

    if (cached) {
        // Serve cached immediately and refresh in background
        console.log(cached);
        api.get(url)
            .then((fresh) => setCache(url, fresh))
            .catch(() => { });

        return cached;
    }
    console.log("No cache found");
    // No cache, fetch normally and store
    const response = await api.get(url);
    setCache(url, response);
    return response;
}